import {
    Box,
    Checkbox,
    FormControl,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    SxProps,
    Theme,
} from '@mui/material';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface FilterOption {
    label: string;
    value: string;
}

interface FilterDropDownProps {
    id: string;
    label: string;
    options: FilterOption[];
    value: string[];
    setValue: (value: string[]) => void;
    disabled?: boolean;
    size?: 'small' | 'medium';
    sx?: SxProps<Theme>;
    renderValue?: (selected: string[]) => ReactNode;
}

export const FilterDropDown = ({
    id,
    label,
    options,
    value,
    setValue,
    disabled = false,
    size = 'small',
    sx,
    renderValue,
}: FilterDropDownProps) => {
    const { t } = useTranslation();

    const labelRenderValue = (selected: string[]) => {
        if (selected.length === 0 || selected.length === options.length) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Box
                        component="span"
                        sx={{ color: '#757575', fontWeight: 400 }}
                    >
                        {label}
                    </Box>
                    <Box
                        component="span"
                        sx={{ color: '#424242', fontWeight: 600 }}
                    >
                        {t('All')}
                    </Box>
                </Box>
            );
        }

        if (selected.length === 1) {
            const option = options.find((opt) => opt.value === selected[0]);
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Box
                        component="span"
                        sx={{ color: '#757575', fontWeight: 400 }}
                    >
                        {label}
                    </Box>
                    <Box
                        component="span"
                        sx={{ color: '#424242', fontWeight: 600 }}
                    >
                        {option ? option.label : selected[0]}
                    </Box>
                </Box>
            );
        }

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box
                    component="span"
                    sx={{ color: '#757575', fontWeight: 400 }}
                >
                    {label}
                </Box>
                <Box
                    component="span"
                    sx={{ color: '#424242', fontWeight: 600 }}
                >
                    {selected.length} {t('selected')}
                </Box>
            </Box>
        );
    };

    return (
        <FormControl
            sx={{
                minWidth: 200,
                ...sx,
            }}
            size={size}
            disabled={disabled}
        >
            <Select
                multiple
                id={id}
                value={value}
                onChange={(event) => setValue(event.target.value as string[])}
                input={<OutlinedInput />}
                renderValue={renderValue || labelRenderValue}
                displayEmpty
                data-testid={`filter-dropdown-${id}`}
                sx={{
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                    backgroundColor: '#F5F5F5',
                    '& .MuiSelect-select': {
                        color: '#424242',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    },
                }}
            >
                {options.map((option) => (
                    <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{
                            fontSize: '14px',
                            padding: '8px 16px',
                            '&:hover': {
                                backgroundColor: '#F5F5F5',
                            },
                            '&.Mui-selected': {
                                backgroundColor: '#E3F2FD',
                                '&:hover': {
                                    backgroundColor: '#E3F2FD',
                                },
                            },
                        }}
                    >
                        <Checkbox
                            checked={value.indexOf(option.value) > -1}
                            sx={{
                                color: '#1976D2',
                                '&.Mui-checked': {
                                    color: '#1976D2',
                                },
                            }}
                        />
                        <ListItemText
                            primary={option.label}
                            sx={{
                                '& .MuiListItemText-primary': {
                                    fontSize: '14px',
                                    color: '#424242',
                                },
                            }}
                        />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};
